import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Clock, CreditCard, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { LogoLockup } from '../components/Logo';
import SquareBookingCTA from '../components/SquareBookingCTA';
import ErrorBoundary from '../components/ErrorBoundary';
import useSEO from '../hooks/useSEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const paymentMethods = [
  'Apple Pay',
  'Google Pay',
  'Interac',
  'Visa',
  'Mastercard',
  'Amex',
  'Discover',
  'JCB'
];

const hours = [
  { day: 'monday', time: '10h – 19h' },
  { day: 'tuesday', time: '10h – 19h' },
  { day: 'wednesday', time: '10h – 19h' },
  { day: 'thursday', time: '10h – 20h' },
  { day: 'friday', time: '10h – 20h' },
  { day: 'saturday', time: '9h – 16h' },
  { day: 'sunday', time: '9h – 16h' },
];

const BookingPage = () => {
  const { t } = useLanguage();
  useSEO({ title: t.seo.booking.title, description: t.seo.booking.description });

  return (
    <div className="min-h-screen pt-20" data-testid="booking-page">
      {/* Hero Header */}
      <section className="py-16 lg:py-24 bg-[#17201a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="deco-line" />
              <span className="text-xs uppercase tracking-[0.25em] text-[#6e756a]">{t.nav.booking}</span>
              <div className="deco-line" />
            </div>
            <h1 className="font-serif text-5xl lg:text-7xl font-light tracking-tighter text-[#f5f0e8] mb-6" data-testid="booking-title">
              {t.booking.title}
            </h1>
            <p className="text-lg text-[#6e756a] font-light max-w-xl mx-auto">
              {t.booking.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Booking Section */}
      <section className="py-16 lg:py-24 bg-[#f5f0e8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Native Booking Flow */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7"
              data-testid="booking-flow-wrapper"
            >
              <ErrorBoundary>
                <SquareBookingCTA />
              </ErrorBoundary>
            </motion.div>

            {/* Sidebar Info */}
            <div className="lg:col-span-5 space-y-8">
              {/* Address */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="p-8 border border-[#6e756a]/20"
                data-testid="booking-address"
              >
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#6e756a] mb-6 flex items-center gap-3">
                  <MapPin size={16} className="text-[#a84b2a]" />
                  {t.booking.addressTitle}
                </h3>
                <address className="not-italic font-serif text-2xl text-[#17201a] leading-relaxed">
                  {t.booking.address}
                </address>
                <p className="text-[#6e756a] font-light mt-2">
                  {t.booking.neighborhood}
                </p>
              </motion.div>

              {/* Hours */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="p-8 border border-[#6e756a]/20"
                data-testid="booking-hours"
              >
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#6e756a] mb-6 flex items-center gap-3">
                  <Clock size={16} className="text-[#a84b2a]" />
                  {t.booking.hoursTitle}
                </h3>
                <ul className="space-y-3">
                  {hours.map((item) => (
                    <li key={item.day} className="flex justify-between text-sm">
                      <span className="text-[#6e756a]">{t.booking.hours[item.day]}</span>
                      <span className="text-[#17201a] font-medium">
                        {item.time || t.booking.hours.closed}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Payment Methods */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="p-8 border border-[#6e756a]/20"
                data-testid="booking-payments"
              >
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#6e756a] mb-6 flex items-center gap-3">
                  <CreditCard size={16} className="text-[#a84b2a]" />
                  {t.booking.paymentsTitle}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {paymentMethods.map((method) => (
                    <span
                      key={method}
                      className="text-xs text-[#17201a]/70 bg-[#17201a]/5 px-3 py-1.5"
                    >
                      {method}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Quick CTA */}
              <Link
                to="/services"
                className="btn-cta w-full flex items-center justify-center gap-3"
              >
                {t.hero.ctaSecondary}
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-[#17201a]" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-4xl lg:text-5xl text-[#f5f0e8] tracking-tight">
              {t.booking.faqTitle}
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-2">
            {t.faq.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-b border-[#6e756a]/20"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left py-6 text-[#f5f0e8] font-light text-lg hover:no-underline hover:text-[#a84b2a] transition-colors [&[data-state=open]]:text-[#a84b2a]">
                  <span className="pr-4">{item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-[#f5f0e8]/60 font-light leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#a84b2a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          {/* Green logo as contrast */}
          <div className="flex justify-center mb-8">
            <LogoLockup variant="green" size="md" />
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl text-[#f5f0e8] mb-6">
            {t.hero.headline}
          </h2>
          <p className="text-[#f5f0e8]/70 font-light mb-8">
            {t.booking.subtitle}
          </p>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
